import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Tabs, Select, Input, InputNumber, Button, Space, Row, Col, Typography, Spinner, message } from '../../ui';
import { X } from 'lucide-react';
import { request } from '../../api/client';

const ITEM_CATEGORIES = ['TRAVEL', 'FOOD', 'LOCAL_CONVEYANCE', 'HOTEL', 'FUEL', 'TOLL_PARKING', 'TRAIN', 'FLIGHT', 'BUS', 'OTHER'];
const DESC_REQUIRED = new Set(['HOTEL', 'OTHER']);
const VISIT_TYPE_OPTIONS = ['FIELD_VISIT', 'TOUR', 'OFFICE', 'OTHER'].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));
const TRAVEL_MODE_OPTIONS = ['CAR', 'BIKE', 'TRAIN', 'FLIGHT', 'BUS', 'TAXI', 'OTHER'].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const FieldLabel = ({ children }: { children: any }) => (
  <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>{children}</Typography.Text>
);

type LineRow = { key: string; id?: string; category: string; description?: string; amount?: number; vendorName?: string; orig?: any };
export type EditTab = 'expenses' | 'details';

// Self-contained draft editor — edits a report's header + line items in one place. Used both
// inline from the Expense Reports table and from the report detail page, so editing never has
// to leave the current screen. The server re-guards (owner/filer + draft/sent-back) on save.
export function EditReportModal({ reportId, initialTab = 'expenses', onClose, onSaved }: {
  reportId: string; initialTab?: EditTab; onClose: () => void; onSaved: () => void;
}) {
  const { data: report, isLoading } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
    refetchOnMount: 'always',
  });
  if (isLoading || !report) {
    return (
      <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Edit report">
        <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>
      </Modal>
    );
  }
  return <EditReportBody report={report} reportId={reportId} initialTab={initialTab} onClose={onClose} onSaved={onSaved} />;
}

function EditReportBody({ report, reportId, initialTab, onClose, onSaved }: {
  report: any; reportId: string; initialTab: EditTab; onClose: () => void; onSaved: () => void;
}) {
  const [tab, setTab] = useState<EditTab>(initialTab);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState<Record<string, any>>({
    visitType: report.visitType ?? undefined,
    travelMode: report.travelMode ?? undefined,
    partyName: report.partyName ?? '',
    partyContact: report.partyContact ?? '',
    partyAddress: report.partyAddress ?? '',
    city: report.city ?? '',
    state: report.state ?? '',
    pincode: report.pincode ?? '',
    workDone: report.workDone ?? '',
    notes: report.notes ?? '',
  });
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const [rows, setRows] = useState<LineRow[]>(() =>
    (report.items ?? []).map((i: any) => ({
      key: `e${i.id}`, id: String(i.id), category: i.category, description: i.description ?? '', amount: Number(i.amount), vendorName: i.vendorName ?? '',
      orig: { category: i.category, description: i.description ?? '', amount: Number(i.amount), vendorName: i.vendorName ?? '' },
    })),
  );
  const [removed, setRemoved] = useState<string[]>([]);
  const upd = (key: string, patch: Partial<LineRow>) => setRows((a) => a.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((a) => [...a, { key: `n${a.length}-${Math.random().toString(36).slice(2)}`, category: 'TRAVEL' }]);
  const removeRow = (r: LineRow) => { if (r.id) setRemoved((d) => [...d, r.id!]); setRows((a) => a.filter((x) => x.key !== r.key)); };

  // One Save: PATCH the header, then reconcile line items (delete removed → patch changed → post new).
  const save = async () => {
    if (!form.visitType) { message.error('Pick a visit type'); setTab('details'); return; }
    for (const r of rows) {
      if (r.amount == null || Number(r.amount) <= 0) { message.error('Every line needs an amount greater than zero'); setTab('expenses'); return; }
      if (!r.category) { message.error('Every line needs a category'); setTab('expenses'); return; }
      if (DESC_REQUIRED.has(r.category) && !r.description?.trim()) { message.error(`${r.category} lines need a description`); setTab('expenses'); return; }
    }
    setBusy(true);
    try {
      await request({ url: `/expense-reports/${reportId}`, method: 'PATCH', data: form });
      for (const delId of removed) await request({ url: `/expense-reports/${reportId}/items/${delId}`, method: 'DELETE' });
      const toUpdate = rows.filter((r) => r.id && r.orig &&
        (r.category !== r.orig.category || (r.description ?? '') !== (r.orig.description ?? '') || Number(r.amount) !== r.orig.amount || (r.vendorName ?? '') !== (r.orig.vendorName ?? '')));
      for (const u of toUpdate) {
        await request({ url: `/expense-reports/${reportId}/items/${u.id}`, method: 'PATCH', data: { category: u.category, description: u.description || undefined, amount: Number(u.amount), vendorName: u.vendorName || undefined } });
      }
      const toCreate = rows.filter((r) => !r.id);
      if (toCreate.length) {
        await request({ url: `/expense-reports/${reportId}/items`, method: 'POST', data: { items: toCreate.map((r) => ({ category: r.category, description: r.description || undefined, amount: Number(r.amount), vendorName: r.vendorName || undefined })) } });
      }
      message.success('Report updated');
      onSaved(); onClose();
    } catch (e: any) { message.error(e?.response?.data?.error?.message ?? 'Failed'); } finally { setBusy(false); }
  };

  const expensesTab = (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {rows.map((r) => (
        <Space key={r.key} wrap align="center" style={{ width: '100%' }}>
          <Select value={r.category} onChange={(v) => upd(r.key, { category: v })} style={{ width: 150 }}
            options={ITEM_CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, ' ') }))} />
          <InputNumber min={1} placeholder="Amount ₹" value={r.amount} style={{ width: 120 }}
            onChange={(v) => upd(r.key, { amount: v == null ? undefined : Number(v) })} />
          <Input placeholder={DESC_REQUIRED.has(r.category) ? 'Description (required)' : 'Description'} value={r.description} style={{ width: 220 }}
            onChange={(e) => upd(r.key, { description: e.target.value })} />
          <Button variant="danger" icon={<X size={16} />} onClick={() => removeRow(r)} aria-label="Remove line" />
        </Space>
      ))}
      <Button variant="default" block onClick={addRow}>+ Add line</Button>
    </Space>
  );

  const detailsTab = (
    <Row gutter={[12, 12]} style={{ marginTop: 4 }}>
      <Col span={12}><FieldLabel>Visit type</FieldLabel>
        <Select style={{ width: '100%' }} placeholder="Visit type" value={form.visitType} onChange={(v) => set('visitType', v)} options={VISIT_TYPE_OPTIONS} /></Col>
      <Col span={12}><FieldLabel>Travel mode</FieldLabel>
        <Select style={{ width: '100%' }} placeholder="Travel mode" allowClear value={form.travelMode || undefined} onChange={(v) => set('travelMode', v ?? '')} options={TRAVEL_MODE_OPTIONS} /></Col>
      <Col span={12}><FieldLabel>Party name</FieldLabel>
        <Input value={form.partyName} onChange={(e) => set('partyName', e.target.value)} placeholder="Customer / party" /></Col>
      <Col span={12}><FieldLabel>Party contact</FieldLabel>
        <Input value={form.partyContact} onChange={(e) => set('partyContact', e.target.value)} placeholder="Phone" /></Col>
      <Col span={24}><FieldLabel>Address</FieldLabel>
        <Input value={form.partyAddress} onChange={(e) => set('partyAddress', e.target.value)} placeholder="Site address" /></Col>
      <Col span={10}><FieldLabel>City</FieldLabel>
        <Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Col>
      <Col span={8}><FieldLabel>State</FieldLabel>
        <Input value={form.state} onChange={(e) => set('state', e.target.value)} /></Col>
      <Col span={6}><FieldLabel>Pincode</FieldLabel>
        <Input value={form.pincode} onChange={(e) => set('pincode', e.target.value)} /></Col>
      <Col span={24}><FieldLabel>Work done</FieldLabel>
        <Input.TextArea rows={2} value={form.workDone} onChange={(e) => set('workDone', e.target.value)} placeholder="What was done on this visit" /></Col>
      <Col span={24}><FieldLabel>Notes</FieldLabel>
        <Input.TextArea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></Col>
    </Row>
  );

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Edit ${report.reportNo}`} width={760} footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>Save</Button></>}>
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as EditTab)}
        items={[
          { key: 'expenses', label: `Expenses (${rows.length})`, children: expensesTab },
          { key: 'details', label: 'Details', children: detailsTab },
        ]}
      />
    </Modal>
  );
}
